import { useForm, useStore } from "@tanstack/react-form";
import { createFileRoute, useRouteContext } from "@tanstack/react-router";
import { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { MemberBadge } from "@/ui/member-badge";
import type { acessosIAQuery } from "./__generated__/acessosIAQuery.graphql";
import type { acessosIAUpdateMutation } from "./__generated__/acessosIAUpdateMutation.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/ia/acessos",
)({
	component: RouteComponent,
});

const accessFormSchema = z
	.object({
		visibility: z.enum(["public", "private"], {
			error: "Tipo de acesso inválido",
		}),
		externalProducts: z.array(z.string()),
		badges: z.array(z.string()),
		tags: z.array(z.string()),
		limitDailyMessages: z.boolean(),
		maxMessagesPerDay: z
			.number()
			.int({ message: "Valor deve ser um número inteiro" })
			.positive({ message: "Valor deve ser maior que zero" })
			.optional(),
		primaryOffer: z.string().optional(),
	})
	.superRefine((val, ctx) => {
		if (val.limitDailyMessages && !val.maxMessagesPerDay) {
			ctx.addIssue({
				code: "custom",
				message: "Informe o limite de mensagens diárias",
				path: ["maxMessagesPerDay"],
			});
		}
	});

function AccessConfigurationCard() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });

	const data = useLazyLoadQuery<acessosIAQuery>(
		graphql`
			query acessosIAQuery($slug: String!) {
				club(slug: $slug) {
					id
					aiPersona {
						id
						visibility
						maxMessagesPerDay
						primaryProduct {
							id
						}
						externalProducts {
							id
							productName
						}
						badges {
							id
							...memberBadgeFragment
						}
						tags {
							id
							text
						}
					}
					badges(first: 10000) {
					  edges {
					    node {
								 id
								 ...memberBadgeFragment
					    }
					  }
					}
					tags(first: 10000) {
					  edges {
							node {
					      id
								text
							}
						}
					}
					offers(first: 10000) {
					  edges {
			        node {
						    id
						    productName
			        }
					  }
					}
				}
			}
		`,
		{ slug: club.slug },
	);

	const aiAccessConfig = data.club.aiPersona;
	const offers = data.club.offers.edges.map((ed) => ed.node) ?? [];
	const badges = data.club.badges.edges.map((ed) => ed.node) ?? [];
	const tags = data.club.tags.edges.map((ed) => ed.node) ?? [];

	const [isEditing, setIsEditing] = useState(false);

	const [updateAccessConfig] = useMutation<acessosIAUpdateMutation>(graphql`
		mutation acessosIAUpdateMutation($input: AiPersonaUpdateInput!) {
			aiPersonaUpdate(input: $input) {
				id
				visibility
				maxMessagesPerDay
				primaryProduct {
					id
					productName
				}
				externalProducts {
					id
					productName
				}
				badges {
					id
				}
				tags {
					id
					text
				}
			}
		}
	`);

	const form = useForm({
		defaultValues: {
			visibility: (aiAccessConfig?.visibility?.toLowerCase() ?? "public") as
				| "public"
				| "private",
			externalProducts:
				aiAccessConfig?.externalProducts?.map((p) => p.id) ?? ([] as string[]),
			badges: aiAccessConfig?.badges?.map((b) => b.id) ?? ([] as string[]),
			tags: aiAccessConfig?.tags?.map((t) => t.id) ?? ([] as string[]),
			limitDailyMessages: !!aiAccessConfig?.maxMessagesPerDay,
			...(aiAccessConfig?.maxMessagesPerDay != null
				? { maxMessagesPerDay: aiAccessConfig.maxMessagesPerDay }
				: {}),
			...(aiAccessConfig?.primaryProduct?.id
				? { primaryOffer: aiAccessConfig.primaryProduct.id }
				: {}),
		},
		validators: {
			onChange: accessFormSchema,
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				if (!data.club.aiPersona?.id) {
					const error = new Error(
						"Configure a IA antes de alterar os acessos.",
					);
					toast.error(error.message);
					reject(error);
					return;
				}
				updateAccessConfig({
					variables: {
						input: {
							id: data.club.aiPersona.id,
							visibility: value.visibility === "public" ? "PUBLIC" : "PRIVATE",
							externalProducts: value.externalProducts,
							badges: value.badges,
							tags: value.tags,
							maxMessagesPerDay: value.limitDailyMessages
								? value.maxMessagesPerDay
								: null,
							primaryOffer: value.primaryOffer || null,
						},
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						toast.success("Configurações de acesso atualizadas com sucesso!");
						setIsEditing(false);
						resolve(response);
					},
					onError: (error) => {
						toast.error("Erro ao atualizar configurações. Tente novamente.");
						reject(error);
					},
				});
			});
		},
	});

	const [visibility, limitDailyMessages] = useStore(form.store, (state) => [
		state.values.visibility,
		state.values.limitDailyMessages,
	]);

	return (
		<form
			className="overflow-hidden rounded-md border py-6 px-6 flex flex-col gap-6"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<div className="flex flex-row items-center justify-between">
				<CardTitle>Configurações de Acesso</CardTitle>

				{isEditing ? (
					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.canSubmit]}
					>
						{([isSubmitting, canSubmit]) => (
							<Button type="submit" disabled={isSubmitting || !canSubmit}>
								{isSubmitting && <Spinner />}
								Salvar
							</Button>
						)}
					</form.Subscribe>
				) : (
					<Button
						type="button"
						variant="outline"
						onClick={() => setIsEditing(true)}
					>
						<Icon name="Pencil" />
						Editar
					</Button>
				)}
			</div>

			<div className="w-full pt-3 flex flex-col gap-6">
				<form.Field name="visibility">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 flex-1">
								<FieldLabel>Tipo de Acesso</FieldLabel>
								<FieldDescription>
									Define quem pode acessar a IA do seu clube.
								</FieldDescription>
							</div>

							<div className="grid grid-cols-2 gap-3 w-full max-w-md">
								<button
									type="button"
									disabled={!isEditing}
									data-checked={field.state.value === "public"}
									className="flex h-[86px] flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary border disabled:opacity-50 disabled:cursor-not-allowed"
									onClick={() => field.setValue("public")}
								>
									<span className="flex flex-col items-center justify-center gap-1 pointer-events-none">
										<Icon name="Globe" className="size-4" />
										<span className="font-medium text-sm">Público</span>
									</span>
									<span className="text-xs text-muted-foreground text-center">
										Todos os membros
									</span>
								</button>

								<button
									type="button"
									disabled={!isEditing}
									data-checked={field.state.value === "private"}
									className="flex h-[86px] flex-col data-[checked=true]:bg-primary/5 items-center justify-center rounded border-muted data-[checked=false]:hover:border-primary/20 data-[checked=true]:border-primary border disabled:opacity-50 disabled:cursor-not-allowed"
									onClick={() => field.setValue("private")}
								>
									<span className="flex flex-col items-center justify-center gap-1 pointer-events-none">
										<Icon name="Lock" className="size-4" />
										<span className="font-medium text-sm">Privado</span>
									</span>
									<span className="text-xs text-muted-foreground text-center">
										Membros específicos
									</span>
								</button>
							</div>
						</Field>
					)}
				</form.Field>

				{visibility === "private" && (
					<>
						<Separator />

						<form.Field name="externalProducts">
							{(field) => (
								<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
									<div className="flex flex-col gap-1.5 flex-1">
										<FieldLabel>Produtos Externos</FieldLabel>
										<FieldDescription>
											Produtos que dão acesso ilimitado à IA.
										</FieldDescription>
									</div>

									<div className="w-full max-w-md">
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="outline"
													disabled={!isEditing}
													className="w-full sm:h-9 h-11 flex items-center justify-between"
												>
													{field.state.value.length > 0 ? (
														<span className="flex-1 line-clamp-1 text-left font-normal">
															{field.state.value
																.map((val) => {
																	const product = offers.find(
																		(p) => p.id === val,
																	);
																	return product?.productName;
																})
																.filter(Boolean)
																.join(", ")}
														</span>
													) : (
														<span className="flex-1 line-clamp-1 text-left font-normal text-muted-foreground">
															Selecionar produtos
														</span>
													)}
													<Icon name="ChevronsUpDown" className="size-4" />
												</Button>
											</PopoverTrigger>

											<PopoverContent
												sideOffset={5}
												className="w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-32px)] overflow-y-auto"
											>
												{offers.length === 0 ? (
													<p className="text-sm text-muted-foreground p-2">
														Nenhum produto disponível
													</p>
												) : (
													offers.map((product) => (
														<button
															type="button"
															key={product.id}
															className="py-2.5 pr-10 line-clamp-1 flex items-center justify-start gap-2 px-2 hover:bg-zinc-950/5 foreground/80 rounded w-full"
															onClick={() => {
																if (!field.state.value.includes(product.id)) {
																	field.pushValue(product.id);
																} else {
																	field.removeValue(
																		field.state.value.indexOf(product.id),
																	);
																}
															}}
														>
															<Checkbox
																checked={field.state.value.includes(product.id)}
																className="pointer-events-none"
															/>
															{product.productName}
														</button>
													))
												)}
											</PopoverContent>
										</Popover>
									</div>
								</Field>
							)}
						</form.Field>

						<form.Field name="badges">
							{(field) => (
								<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
									<div className="flex flex-col gap-1.5 flex-1">
										<FieldLabel>Emblemas</FieldLabel>
										<FieldDescription>
											Emblemas que dão acesso ilimitado à IA.
										</FieldDescription>
									</div>

									<div className="w-full max-w-md">
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="outline"
													disabled={!isEditing}
													className="w-full sm:h-9 h-11 flex items-center justify-between"
												>
													{field.state.value.length > 0 ? (
														<div className="flex-1 line-clamp-1 text-left font-normal gap-2 flex flex-row items-center justify-start overflow-hidden">
															{field.state.value
																.map((val) => {
																	const badge = badges.find(
																		(b) => b.id === val,
																	);
																	if (!badge) return null;
																	return (
																		<MemberBadge key={badge.id} badge={badge} />
																	);
																})
																.filter(Boolean)}
														</div>
													) : (
														<span className="flex-1 line-clamp-1 text-left font-normal text-muted-foreground">
															Selecionar emblemas
														</span>
													)}
													<Icon name="ChevronsUpDown" className="size-4" />
												</Button>
											</PopoverTrigger>

											<PopoverContent
												sideOffset={5}
												className="w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-32px)] overflow-y-auto"
											>
												{badges.length === 0 ? (
													<p className="text-sm text-muted-foreground p-2">
														Nenhum emblema disponível
													</p>
												) : (
													badges.map((badge) => (
														<button
															type="button"
															key={badge.id}
															className="py-2.5 pr-10 flex items-center justify-start gap-2 px-2 hover:bg-zinc-950/5 foreground/80 rounded w-full"
															onClick={() => {
																if (!field.state.value.includes(badge.id)) {
																	field.pushValue(badge.id);
																} else {
																	field.removeValue(
																		field.state.value.indexOf(badge.id),
																	);
																}
															}}
														>
															<Checkbox
																checked={field.state.value.includes(badge.id)}
																className="pointer-events-none"
															/>
															<MemberBadge badge={badge} />
														</button>
													))
												)}
											</PopoverContent>
										</Popover>
									</div>
								</Field>
							)}
						</form.Field>

						<form.Field name="tags">
							{(field) => (
								<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
									<div className="flex flex-col gap-1.5 flex-1">
										<FieldLabel>Tags</FieldLabel>
										<FieldDescription>
											Tags que dão acesso ilimitado à IA.
										</FieldDescription>
									</div>

									<div className="w-full max-w-md">
										<Popover>
											<PopoverTrigger asChild>
												<Button
													type="button"
													variant="outline"
													disabled={!isEditing}
													className="w-full sm:h-9 h-11 flex items-center justify-between"
												>
													{field.state.value.length > 0 ? (
														<span className="flex-1 line-clamp-1 text-left font-normal">
															{field.state.value
																.map((val) => {
																	const tag = tags.find((t) => t.id === val);
																	return tag?.text;
																})
																.filter(Boolean)
																.join(", ")}
														</span>
													) : (
														<span className="flex-1 line-clamp-1 text-left font-normal text-muted-foreground">
															Selecionar tags
														</span>
													)}
													<Icon name="ChevronsUpDown" className="size-4" />
												</Button>
											</PopoverTrigger>

											<PopoverContent
												sideOffset={5}
												className="w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-32px)] overflow-y-auto"
											>
												{tags.length === 0 ? (
													<p className="text-sm text-muted-foreground p-2">
														Nenhuma tag disponível
													</p>
												) : (
													tags.map((tag) => (
														<button
															type="button"
															key={tag.id}
															className="py-2.5 pr-10 line-clamp-1 flex items-center justify-start gap-2 px-2 hover:bg-zinc-950/5 foreground/80 rounded w-full"
															onClick={() => {
																if (!field.state.value.includes(tag.id)) {
																	field.pushValue(tag.id);
																} else {
																	field.removeValue(
																		field.state.value.indexOf(tag.id),
																	);
																}
															}}
														>
															<Checkbox
																checked={field.state.value.includes(tag.id)}
																className="pointer-events-none"
															/>
															{tag.text}
														</button>
													))
												)}
											</PopoverContent>
										</Popover>
									</div>
								</Field>
							)}
						</form.Field>
					</>
				)}

				<Separator />

				<form.Field name="limitDailyMessages">
					{(field) => (
						<Field
							orientation="horizontal"
							className="flex flex-col lg:flex-row lg:gap-6 lg:items-center lg:justify-between"
						>
							<div className="flex flex-col gap-1.5 flex-1">
								<FieldLabel>Limitar Mensagens Diárias</FieldLabel>
								<FieldDescription>
									Define um limite de mensagens que cada membro pode enviar por
									dia.
								</FieldDescription>
							</div>

							<Switch
								disabled={!isEditing}
								checked={field.state.value}
								onCheckedChange={(val) => field.setValue(val)}
							/>
						</Field>
					)}
				</form.Field>

				{limitDailyMessages && (
					<form.Field name="maxMessagesPerDay">
						{(field) => (
							<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
								<div className="flex flex-col gap-1.5 flex-1">
									<FieldLabel>Máximo de Mensagens por Dia</FieldLabel>
									<FieldDescription>
										Quantidade máxima de mensagens que um membro pode enviar.
									</FieldDescription>
								</div>

								<div className="flex flex-col gap-1.5 w-full max-w-md">
									<Input
										type="number"
										min={1}
										id={field.name}
										name={field.name}
										value={field.state.value ?? 0}
										onBlur={field.handleBlur}
										onChange={(e) => {
											const parsed = parseInt(e.target.value, 10);

											if (Number.isNaN(parsed)) {
												field.handleChange(0);
											} else {
												field.handleChange(parsed);
											}
										}}
										placeholder="Ex: 50"
										disabled={!isEditing}
										aria-invalid={
											!!(
												field.state.meta.isTouched && field.state.meta.errors[0]
											)
										}
									/>
									{field.state.meta.isTouched && field.state.meta.errors[0] ? (
										<FieldError errors={field.state.meta.errors} />
									) : null}
								</div>
							</Field>
						)}
					</form.Field>
				)}

				<Separator />

				<form.Field name="primaryOffer">
					{(field) => (
						<Field className="flex flex-col lg:flex-row lg:gap-6 lg:items-start lg:justify-between">
							<div className="flex flex-col gap-1.5 flex-1">
								<FieldLabel>Oferta Principal</FieldLabel>
								<FieldDescription>
									Oferta exibida quando o membro atinge o limite de mensagens ou
									não tem acesso à IA.
								</FieldDescription>
							</div>

							<div className="w-full max-w-md">
								<Popover>
									<PopoverTrigger asChild>
										<Button
											type="button"
											variant="outline"
											disabled={!isEditing}
											className="w-full sm:h-9 h-11 flex items-center justify-between"
										>
											{field.state.value ? (
												<span className="flex-1 line-clamp-1 text-left font-normal">
													{offers.find((o) => o.id === field.state.value)
														?.productName ?? "Oferta selecionada"}
												</span>
											) : (
												<span className="flex-1 line-clamp-1 text-left font-normal text-muted-foreground">
													Selecionar oferta
												</span>
											)}
											<Icon name="ChevronsUpDown" className="size-4" />
										</Button>
									</PopoverTrigger>

									<PopoverContent
										sideOffset={5}
										className="w-(--radix-popover-trigger-width) max-h-[calc(var(--radix-popover-content-available-height)-32px)] overflow-y-auto"
									>
										{offers.length === 0 ? (
											<p className="text-sm text-muted-foreground p-2">
												Nenhuma oferta disponível
											</p>
										) : (
											<>
												{field.state.value && (
													<button
														type="button"
														className="py-2.5 pr-10 line-clamp-1 flex items-center justify-start gap-2 px-2 hover:bg-zinc-950/5 text-muted-foreground rounded w-full"
														onClick={() => field.setValue(undefined)}
													>
														<Icon name="X" className="size-4" />
														Remover seleção
													</button>
												)}
												{offers.map((offer) => (
													<button
														type="button"
														key={offer.id}
														className="py-2.5 pr-10 line-clamp-1 flex items-center justify-start gap-2 px-2 hover:bg-zinc-950/5 foreground/80 rounded w-full"
														onClick={() => field.setValue(offer.id)}
													>
														<Checkbox
															checked={field.state.value === offer.id}
															className="pointer-events-none"
														/>
														{offer.productName}
													</button>
												))}
											</>
										)}
									</PopoverContent>
								</Popover>
							</div>
						</Field>
					)}
				</form.Field>
			</div>
		</form>
	);
}

function RouteComponent() {
	return (
		<div className="p-6 flex flex-col gap-6">
			<AccessConfigurationCard />
		</div>
	);
}
